import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalErrorHandlerService } from '@app/core/services';
import { ActivityService } from '@app/core/http-services/activity.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Highcharts from 'highcharts';
import noDataModule from 'highcharts/modules/no-data-to-display';
import exportModule from 'highcharts/modules/exporting';
import { AreaGraphOption } from '@app/shared/utilities/graph-option';
import { UserService } from '@app/core/http-services/user.service';
import moment from 'moment';
exportModule(Highcharts);
noDataModule(Highcharts);

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
})
export class ActivityComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  breadcrumbs: any = [
    {
      label: 'Activity',
      active: true,
    },
  ];
  loader: boolean = false;
  statLoader: boolean = false;
  historyData: any;
  totalUsers: number = 0;
  totalActiveUsers: any = 0;
  stats: any;
  activeUsersData: any = [];
  signupsData: any = [];
  retentionData: any = [];
  failedLoginData: any = [];
  dailyActiveUserCount: number = 0;
  retentionCount: number = 0;
  signupCount: number = 0;
  failedLoginCount: number = 0;
  Highcharts: typeof Highcharts = Highcharts;
  activeUsersChartOptions: any = JSON.parse(JSON.stringify(AreaGraphOption.WIND_CHART));
  signupsChartOptions: any = JSON.parse(JSON.stringify(AreaGraphOption.WIND_CHART));
  retentionChartOptions: any = JSON.parse(JSON.stringify(AreaGraphOption.WIND_CHART));
  failedLoginChartOptions: any = JSON.parse(JSON.stringify(AreaGraphOption.WIND_CHART));
  activityText: string;

  constructor(
    private activityService: ActivityService,
    private errorHandlerService: GlobalErrorHandlerService,
    private userService: UserService
  ) {}
  ngOnInit(): void {
    this.getActivityHistory();
    this.getTotalActiveUsers();
    this.getStatsOverview();
    this.getUsersCount();
  }

  updateChartData() {
    this.activeUsersChartOptions = {
      ...this.activeUsersChartOptions,
      tooltip: AreaGraphOption.TOOLTIP,
      xAxis: AreaGraphOption.xAxis,
    };
    this.retentionChartOptions = {
      ...this.retentionChartOptions,
      tooltip: AreaGraphOption.TOOLTIP,
      xAxis: AreaGraphOption.xAxis,
    };
    this.signupsChartOptions = {
      ...this.signupsChartOptions,
      tooltip: AreaGraphOption.TOOLTIP,
      xAxis: AreaGraphOption.xAxis,
    };
    this.failedLoginChartOptions = {
      ...this.failedLoginChartOptions,
      tooltip: AreaGraphOption.TOOLTIP,
      xAxis: AreaGraphOption.xAxis,
    };
    this.activeUsersChartOptions.series[0].data = this.activeUsersData;
    this.activeUsersChartOptions.series[0]['name'] = 'Users';
    this.retentionChartOptions.series[0].data = this.retentionData;
    this.retentionChartOptions.series[0]['name'] = 'Retention Rate';
    this.retentionChartOptions.yAxis.title.text = 'Values (%)';
    this.signupsChartOptions.series[0].data = this.signupsData;
    this.signupsChartOptions.series[0]['name'] = 'Signups';
    this.failedLoginChartOptions.series[0].data = this.failedLoginData;
    this.failedLoginChartOptions.series[0]['name'] = 'Failed Logins';

    Highcharts.chart('dailyActiveUserChart', this.activeUsersChartOptions);
    Highcharts.chart('retentionChart', this.retentionChartOptions);
    Highcharts.chart('signupChart', this.signupsChartOptions);
    Highcharts.chart('failedLoginChart', this.failedLoginChartOptions);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Get Activity History
   */
  getActivityHistory() {
    this.loader = true;
    this.activityService
      .getActivityHistoryApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.historyData = response;
          this.setActivityText();
          this.formatChartData(response);
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          this.errorHandlerService.apiErrorHandle(err);
        }
      );
  }

  /**
   * Format chart data
   */
  formatChartData(historyData) {
    this.activeUsersData = [];
    this.signupsData = [];
    this.retentionData = [];
    this.failedLoginData = [];
    let dailyActiveUsers: number = 0;
    let retentionUsers: number = 0;
    if (historyData?.fields && historyData?.values) {
      historyData.fields.forEach((element1, index1) => {
        historyData.values.forEach((element2) => {
          let obj: any = [];
          if (index1 > 0 && element1 == 'active_users') {
            obj[0] = element2[0];
            obj[1] = element2[index1];
            dailyActiveUsers += element2[index1];
            this.activeUsersData.push(obj);
          }
          if (index1 > 0 && element1 == 'failed_logins') {
            obj[0] = element2[0];
            obj[1] = element2[index1];
            this.failedLoginCount += element2[index1];
            this.failedLoginData.push(obj);
          }
          if (index1 > 0 && element1 == 'new_signups') {
            obj[0] = element2[0];
            obj[1] = element2[index1];
            this.signupCount += element2[index1];
            this.signupsData.push(obj);
          }
          if (index1 > 0 && element1 == 'user_retention') {
            obj[0] = element2[0];
            obj[1] = element2[index1] * 100;
            retentionUsers += element2[index1];
            this.retentionData.push(obj);
          }
        });
      });
      this.dailyActiveUserCount = Math.round(dailyActiveUsers / historyData.values.length);
      this.retentionCount = Number(retentionUsers.toFixed(2));
      this.updateChartData();
    } else {
      this.updateChartData();
    }
  }

  /**
   * Get Activity History
   */
  getTotalActiveUsers() {
    this.activityService
      .getTotalActiveUsersApi()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.totalActiveUsers = response;
        },
        (err) => {
          this.errorHandlerService.apiErrorHandle(err);
        }
      );
  }

  /**
   * Get Activity History
   */
  getStatsOverview() {
    this.statLoader = true;
    let params: any = {
      metrics: ['clients', 'connections', 'resource_servers'].join(','),
    };
    this.activityService
      .getStatsOverviewApi(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.stats = response;
          this.statLoader = false;
        },
        (err) => {
          this.statLoader = false;
          this.errorHandlerService.apiErrorHandle(err);
        }
      );
  }
  /**
   * Get all the users
   */
  getUsersCount() {
    this.loader = true;
    let params = { include_totals: true };
    this.userService
      .getAllUsers(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.totalUsers = response.total;
        },
        (err) => {
          this.errorHandlerService.apiErrorHandle(err);
        }
      );
  }

  /**
   * Set activity text from date to to date
   */
  setActivityText() {
    if (this.historyData?.values?.length) {
      let fromDate: any = this.historyData?.values[0][0];
      let toDate: any = this.historyData?.values[this.historyData?.values?.length - 1][0];
      this.activityText = `( Activities from ${moment
        .unix(fromDate)
        .local()
        .format('MMM D, YYYY')} To ${moment.unix(toDate).local().format('MMM D, YYYY')} )`;
    }
  }
}
